import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useAuth } from '../context/AuthContext';
import Aide from './Aide';
import Layout, { ETAPES } from './Layout';
import Portefeuille from './Portefeuille';
import Declaration from './etapes/Declaration';
import Chiffrage from './etapes/Chiffrage';
import Contenu from './etapes/Contenu';
import Contrat from './etapes/Contrat';
import Dommages from './etapes/Dommages';
import FraisAnnexes from './etapes/FraisAnnexes';
import Intervenants from './etapes/Intervenants';
import Dossier from './etapes/Dossier';
import Logement from './etapes/Logement';
import ReponseAssureur from './etapes/ReponseAssureur';
import Partage from './Partage';
import Reclamation from './Reclamation';
import Suivi from './Suivi';
import {
  annulerDossier, creerDossier, lireProfil, listerMesDossiers,
} from './api';

/** Écrans encore à construire : on le dit plutôt que de laisser une page vide. */
function AConstruire({ etape }) {
  const titre = ETAPES.find((e) => e.cle === etape)?.titre || '';
  return (
    <Layout etape={etape} titre={titre}>
      <Alert severity="info">
        Cette étape n'est pas encore disponible.
      </Alert>
    </Layout>
  );
}

/**
 * Orchestre le parcours d'un dossier : une étape à la fois, dans l'ordre.
 *
 * L'assuré n'a pas à comprendre la structure du dossier — il répond à des
 * questions, et le dossier se construit derrière.
 */
export default function ParcoursAssure() {
  const { expertiseId, etape = 'sinistre' } = useParams();
  const naviguer = useNavigate();

  const allerA = useCallback(
    (cle) => naviguer(`/mon-dossier/${expertiseId}/${cle}`),
    [naviguer, expertiseId],
  );

  const suivante = useCallback(() => {
    const index = ETAPES.findIndex((e) => e.cle === etape);
    const prochaine = ETAPES[Math.min(index + 1, ETAPES.length - 1)];
    allerA(prochaine.cle);
  }, [etape, allerA]);

  const precedente = useCallback(() => {
    const index = ETAPES.findIndex((e) => e.cle === etape);
    allerA(ETAPES[Math.max(index - 1, 0)].cle);
  }, [etape, allerA]);

  const communs = { expertiseId, onSuivant: suivante, onRetour: precedente,
                    onEtape: allerA };

  if (etape === 'sinistre') {
    return <Declaration expertiseId={expertiseId} onSuivant={suivante}
                        onEtape={allerA} />;
  }
  if (etape === 'contrat') return <Contrat {...communs} />;
  if (etape === 'intervenants') return <Intervenants {...communs} />;
  if (etape === 'logement') return <Logement {...communs} />;
  if (etape === 'dommages') return <Dommages {...communs} />;
  if (etape === 'contenu') return <Contenu {...communs} />;
  if (etape === 'chiffrage') return <Chiffrage {...communs} />;
  if (etape === 'frais') return <FraisAnnexes {...communs} />;
  if (etape === 'reclamation' || etape === 'declaration-sinistre') {
    return <Reclamation expertiseId={expertiseId}
                        mode={etape === 'declaration-sinistre'
                          ? 'declaration' : 'reclamation'}
                        onRetour={() => allerA('dossier')}
                        onSuivi={() => allerA('suivi')} />;
  }
  if (etape === 'aide' || etape === 'assistance') {
    // Deux portes vers le même écran : « j'ai une question » l'ouvre tel
    // quel, « aidez-moi » ouvre d'emblée la demande d'accompagnement.
    return <Aide expertiseId={expertiseId} onEtape={allerA}
                 intention={etape === 'assistance' ? 'ASSISTANCE' : null}
                 onRetour={() => naviguer(-1)} />;
  }
  if (etape === 'partage') {
    return <Partage expertiseId={expertiseId}
                    onRetour={() => allerA('dossier')} />;
  }
  if (etape === 'suivi') {
    return <Suivi expertiseId={expertiseId} onEtape={allerA}
                  onRetour={() => allerA('dossier')} />;
  }
  if (etape === 'reponse') return <ReponseAssureur {...communs} />;
  if (etape === 'dossier') return <Dossier {...communs} />;
  return <AConstruire etape={etape} />;
}

/**
 * Point d'entrée, selon qui se connecte.
 *
 * Un assuré a un sinistre et le déclare. Un intermédiaire en suit trente et
 * cherche celui qui est bloqué : ce n'est pas le même écran.
 */
export function AccueilAssure() {
  const { organization } = useAuth();
  if (organization?.role === 'INTERMEDIAIRE') return <Portefeuille />;
  return <AccueilSinistre />;
}

/** L'assuré : reprendre son dossier, ou en ouvrir un. */
function AccueilSinistre() {
  const naviguer = useNavigate();
  const [dossiers, setDossiers] = useState(null);
  const [erreur, setErreur] = useState('');
  const [creation, setCreation] = useState(false);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    let vivant = true;
    listerMesDossiers()
      .then((liste) => vivant && setDossiers(liste))
      .catch(() => vivant && setErreur('Impossible de charger vos dossiers.'));
    // On annonce le droit avant le clic : découvrir un refus après avoir
    // raconté son sinistre serait la pire façon de l'apprendre.
    lireProfil()
      .then((p) => vivant && setQuota(p.quota))
      .catch(() => {});
    return () => { vivant = false; };
  }, []);

  const ouvrir = async () => {
    if (creation) return;
    setCreation(true);
    setErreur('');
    try {
      // Le serveur rend le dossier vierge existant s'il y en a un : deux
      // clics ne font pas deux sinistres.
      const dossier = await creerDossier({ name: 'Mon sinistre' });
      naviguer(`/mon-dossier/${dossier.id}/sinistre`);
    } catch (e) {
      setErreur(e?.response?.data?.message
        || "L'ouverture n'a pas abouti. Réessayez.");
      setCreation(false);
    }
  };

  const annuler = async (dossier) => {
    setErreur('');
    try {
      await annulerDossier(dossier.id);
      setDossiers((liste) => liste.filter((d) => d.id !== dossier.id));
    } catch {
      setErreur("L'annulation n'a pas abouti.");
    }
  };

  return (
    <Layout
      titre="Votre sinistre"
      introduction="Nous allons constituer votre dossier ensemble, étape par étape : ce qui s'est passé, votre logement, les dommages, puis leur chiffrage."
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}

      <Stack spacing={3}>
        {dossiers === null && !erreur && <CircularProgress size={28} />}

        {dossiers?.map((d) => (
          <Paper key={d.id} variant="outlined" sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                   alignItems={{ sm: 'center' }} justifyContent="space-between">
              <div>
                <Typography variant="h3">{d.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {d.reference ? `Référence ${d.reference} · ` : ''}{d.statut}
                </Typography>
              </div>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="contained"
                        onClick={() => naviguer(`/mon-dossier/${d.id}/sinistre`)}>
                  Reprendre
                </Button>
                <Button variant="outlined"
                        onClick={() => naviguer(`/mon-dossier/${d.id}/suivi`)}>
                  Suivi
                </Button>
                <Button variant="outlined"
                        onClick={() => naviguer(`/mon-dossier/${d.id}/partage`)}>
                  Inviter
                </Button>
                <Button color="inherit" onClick={() => annuler(d)}>
                  Annuler
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}

        <Paper variant="outlined"
               sx={{ p: 3, borderStyle: 'dashed', textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {dossiers?.length ? 'Un autre sinistre ?' : "Vous venez de subir un sinistre ?"}
          </Typography>
          <Button variant="contained" size="large" onClick={ouvrir}
                  disabled={creation}>
            {creation ? 'Ouverture…' : 'Déclarer un sinistre'}
          </Button>

          {quota?.soumis && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {quota.droit
                ? 'Votre sinistre de l\u2019année est offert.'
                : `Votre sinistre offert est déjà ouvert${
                    quota.prochain_gratuit
                      ? ` — le prochain le sera le ${
                          new Date(quota.prochain_gratuit)
                            .toLocaleDateString('fr-BE')}`
                      : ''}. Un sinistre supplémentaire coûte ${quota.prix} €.`}
            </Typography>
          )}
        </Paper>
      </Stack>
    </Layout>
  );
}
